import React, { useState } from 'react';
import {
    Box,
    Button,
    Alert,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Divider,
    TextField,
    InputAdornment,
    Typography,
    Paper,
    FormControlLabel
} from '@mui/material';
import { SearchOutlinedIcon, AutorenewOutlinedIcon, CheckCircleOutlineIcon, ErrorIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import { MicrosoftAzure } from 'utils/icons/integrationsSvgIcons';

const TeamV2SyncAdContent = ({ onDataChange, onValidationChange }) => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
    const [adUsers, setAdUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sendInvites, setSendInvites] = useState(true);

    // Mock AD users for demonstration
    const mockAdUsers = [
        { id: '1', displayName: 'John Smith', email: 'john.smith@company.com', department: 'Engineering', jobTitle: 'Senior Developer' },
        { id: '2', displayName: 'Sarah Johnson', email: 'sarah.johnson@company.com', department: 'Marketing', jobTitle: 'Marketing Manager' },
        { id: '3', displayName: 'Mike Wilson', email: 'mike.wilson@company.com', department: 'Sales', jobTitle: 'Sales Representative' },
        { id: '4', displayName: 'Emily Davis', email: 'emily.davis@company.com', department: 'HR', jobTitle: 'HR Specialist' },
        { id: '5', displayName: 'David Brown', email: 'david.brown@company.com', department: 'Engineering', jobTitle: 'DevOps Engineer' },
    ];

    const connectToAd = async () => {
        setConnectionStatus('connecting');
        setError('');
        setLoading(true);

        try {
            // Simulate AD connection
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Mock successful connection
            setConnectionStatus('connected');
            setAdUsers(mockAdUsers);
            onDataChange([]);
            onValidationChange(false);
        } catch (err) {
            setConnectionStatus('error');
            setError('Failed to connect to Microsoft Active Directory. Please check your credentials and try again.');
            onDataChange([]);
            onValidationChange(false);
        } finally {
            setLoading(false);
        }
    };

    const handleUserToggle = (userId) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(userId)) {
            newSelected.delete(userId);
        } else {
            newSelected.add(userId);
        }
        setSelectedUsers(newSelected);

        // Update parent with selected user data
        const selectedUserData = adUsers.filter(user => newSelected.has(user.id));
        onDataChange(selectedUserData);
        onValidationChange(selectedUserData.length > 0);
    };

    const handleSelectAll = () => {
        const filteredUserIds = filteredUsers.map(user => user.id);
        let newSelected;

        if (selectedUsers.size === filteredUserIds.length) {
            newSelected = new Set();
        } else {
            newSelected = new Set(filteredUserIds);
        }

        setSelectedUsers(newSelected);

        // Update parent
        const selectedUserData = adUsers.filter(user => newSelected.has(user.id));
        onDataChange(selectedUserData);
        onValidationChange(selectedUserData.length > 0);
    };

    const filteredUsers = adUsers.filter(user =>
        user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.department.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box sx={{ padding: '2rem', paddingTop: '3rem' }}>
            {/* Heading */}
            <Typography variant="body2" sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '2rem'
            }}>
                Sync with Microsoft Active Directory
            </Typography>

            {/* Icon Container */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                marginBottom: '1.5rem',
                backgroundColor: '#fff',
                borderRadius: '0.5rem',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e0e0e0',
                width: '50%'
            }}>
                <MicrosoftAzure
                    width="3rem"
                    height="3rem"
                />
                <Typography variant="h6" sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#333',
                    marginTop: '1rem',
                    textAlign: 'center'
                }}>
                    Active Directory
                </Typography>
                <Typography variant="body2" sx={{
                    fontSize: '0.875rem',
                    color: '#666',
                    marginTop: '0.5rem',
                    textAlign: 'center',
                    lineHeight: 1.4
                }}>
                    Full platform for marketing, sales, customer service, and CRM software.
                </Typography>

                {/* Send Email Invites Checkbox */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendInvites}
                            onChange={(e) => setSendInvites(e.target.checked)}
                            sx={{
                                color: '#e0e0e0',
                                '&.Mui-checked': {
                                    color: '#000',
                                },
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Typography variant="body2" sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#828282'
                            }}>
                                Send email invites
                            </Typography>
                            <InfoOutlinedIcon sx={{
                                fontSize: '1.25rem',
                                color: '#999',
                                cursor: 'pointer'
                            }} />
                        </Box>
                    }
                    sx={{ marginTop: '1rem' }}
                />

                {/* Action Buttons */}
                <Box sx={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '0.75rem',
                    justifyContent: 'center'
                }}>
                    {/* Configure Button (White) */}
                    <Button
                        variant="outlined"
                        sx={{
                            backgroundColor: 'white',
                            color: '#4f4f4f',
                            borderRadius: '1.25rem',
                            border: '1px solid #e3e3e3',
                            textTransform: 'none',
                            padding: '8px 24px',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                borderColor: '#d0d0d0',
                            },
                        }}
                    >
                        Configure
                    </Button>

                    {/* Edit Setup Button (Dark) */}
                    <Button
                        variant="contained"
                        disabled={!sendInvites}
                        sx={{
                            backgroundColor: sendInvites ? '#000' : '#e0e0e0',
                            color: sendInvites ? '#fff' : '#999',
                            borderRadius: '1.25rem',
                            textTransform: 'none',
                            padding: '8px 24px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: sendInvites ? '#333' : '#e0e0e0',
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#999',
                            }
                        }}
                    >
                        Edit Setup
                    </Button>
                </Box>
            </Box>

            {/* Connection Status */}
            <Box sx={{ marginBottom: '1.5rem' }}>
                {connectionStatus === 'disconnected' && (
                    <Box>
                        {/* Connect button removed */}
                    </Box>
                )}

                {connectionStatus === 'connecting' && (
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', marginBottom: '0.75rem' }}>
                            Connecting to Microsoft Active Directory...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {connectionStatus === 'connected' && (
                    <Alert
                        severity="success"
                        icon={<CheckCircleOutlineIcon />}
                        sx={{ marginBottom: '1rem' }}
                    >
                        Successfully connected to Microsoft Active Directory
                    </Alert>
                )}

                {connectionStatus === 'error' && (
                    <Alert
                        severity="error"
                        icon={<ErrorIcon />}
                        sx={{ marginBottom: '1rem' }}
                    >
                        Connection failed. Please try again.
                    </Alert>
                )}
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ marginBottom: '1rem' }}>
                    {error}
                </Alert>
            )}

            {/* User Selection */}
            {connectionStatus === 'connected' && (
                <Box>
                    {/* Search */}
                    <Box sx={{ marginBottom: '1rem' }}>
                        <TextField
                            fullWidth
                            placeholder="Search users by name, email, or department..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon sx={{ color: '#999' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                    </Box>

                    {/* Select All */}
                    <Box sx={{ marginBottom: '1rem' }}>
                        <Button
                            variant="text"
                            onClick={handleSelectAll}
                            sx={{
                                textTransform: 'none',
                                color: '#666',
                            }}
                        >
                            {selectedUsers.size === filteredUsers.length ? 'Deselect All' : 'Select All'}
                            {filteredUsers.length > 0 && ` (${filteredUsers.length})`}
                        </Button>
                    </Box>

                    {/* Users List */}
                    <Paper
                        variant="outlined"
                        sx={{
                            maxHeight: '25rem',
                            overflow: 'auto',
                            marginBottom: '1.5rem',
                            borderRadius: '0.5rem',
                        }}
                    >
                        <List>
                            {filteredUsers.map((user, index) => (
                                <Box key={user.id}>
                                    <ListItem
                                        button
                                        onClick={() => handleUserToggle(user.id)}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: '#f9f9f9',
                                            },
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                checked={selectedUsers.has(user.id)}
                                                onChange={() => handleUserToggle(user.id)}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={user.displayName}
                                            secondary={
                                                <Box>
                                                    <Typography variant="caption" display="block">
                                                        {user.email}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        {user.jobTitle} • {user.department}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < filteredUsers.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    </Paper>

                    {/* Selection Summary */}
                    {selectedUsers.size > 0 && (
                        <Box sx={{
                            backgroundColor: '#f9f9f9',
                            padding: '1rem',
                            borderRadius: '0.5rem'
                        }}>
                            <Typography variant="body2" sx={{ color: '#333' }}>
                                <strong>{selectedUsers.size}</strong> user{selectedUsers.size !== 1 ? 's' : ''} selected for sync
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default TeamV2SyncAdContent;
